/**
 * Contas bancárias da empresa (D100): mais de uma conta, uma marcada como principal (a sugerida na proposta).
 * Ao gerar a proposta, escolhe-se qual conta sai nela. Só o admin cadastra e altera.
 */
import { useState } from "react";
import { useGetList, useNotify, usePermissions } from "react-admin";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/AddOutlined";
import EstrelaIcon from "@mui/icons-material/Star";
import EstrelaVaziaIcon from "@mui/icons-material/StarBorder";
import type { Permissoes } from "../lib/provedores";
import { supabase } from "../lib/supabase";

export interface ContaBancaria {
  id: string;
  banco: string;
  banco_codigo: string | null;
  agencia: string;
  conta: string;
  tipo: string;
  pix: string | null;
  principal: boolean;
  ativo: boolean;
  observacao: string | null;
}

export const TIPOS_CONTA = [
  { id: "corrente", name: "Conta corrente" },
  { id: "poupanca", name: "Poupança" },
  { id: "pagamento", name: "Conta de pagamento" },
];

/** "Banco Itaú (341) · Ag. 0001 · C/C 12345-6" — como sai na proposta. */
export function textoConta(c: Pick<ContaBancaria, "banco" | "banco_codigo" | "agencia" | "conta" | "tipo">): string {
  const tipo = c.tipo === "poupanca" ? "Poupança" : c.tipo === "pagamento" ? "Conta" : "C/C";
  return `${c.banco}${c.banco_codigo ? ` (${c.banco_codigo})` : ""} · Ag. ${c.agencia} · ${tipo} ${c.conta}`;
}

interface Campos {
  banco: string;
  banco_codigo: string;
  agencia: string;
  conta: string;
  tipo: string;
  pix: string;
  observacao: string;
  principal: boolean;
  ativo: boolean;
}

export function DialogoConta({ conta, primeira, aoFechar, aoGravar }: { conta: ContaBancaria | null; primeira: boolean; aoFechar: () => void; aoGravar: () => void }) {
  const notify = useNotify();
  const [c, setC] = useState<Campos>({
    banco: conta?.banco ?? "",
    banco_codigo: conta?.banco_codigo ?? "",
    agencia: conta?.agencia ?? "",
    conta: conta?.conta ?? "",
    tipo: conta?.tipo ?? "corrente",
    pix: conta?.pix ?? "",
    observacao: conta?.observacao ?? "",
    principal: conta?.principal ?? primeira,
    ativo: conta?.ativo ?? true,
  });
  const [gravando, setGravando] = useState(false);
  const codigoInvalido = c.banco_codigo !== "" && !/^[0-9]{3}$/.test(c.banco_codigo);
  const muda = (campo: keyof Campos) => (e: { target: { value: string } }) => setC((x) => ({ ...x, [campo]: e.target.value }));

  async function gravar() {
    setGravando(true);
    const dados = {
      banco: c.banco.trim(),
      banco_codigo: c.banco_codigo || null,
      agencia: c.agencia.trim(),
      conta: c.conta.trim(),
      tipo: c.tipo,
      pix: c.pix.trim() || null,
      observacao: c.observacao.trim() || null,
      principal: c.ativo && c.principal,
      ativo: c.ativo,
    };
    const { error } = conta ? await supabase.from("conta_bancaria").update(dados).eq("id", conta.id) : await supabase.from("conta_bancaria").insert(dados);
    setGravando(false);
    if (error) {
      notify(`Não foi possível gravar: ${error.message}`, { type: "error" });
      return;
    }
    notify("Conta gravada.", { type: "success" });
    aoGravar();
  }

  return (
    <Dialog open onClose={gravando ? undefined : aoFechar} fullWidth maxWidth="sm">
      <DialogTitle>{conta ? "Conta bancária" : "Nova conta bancária"}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid size={{ xs: 8 }}>
            <TextField label="Banco" value={c.banco} onChange={muda("banco")} required fullWidth autoFocus placeholder="Ex.: Banco Itaú" />
          </Grid>
          <Grid size={{ xs: 4 }}>
            <TextField
              label="Código"
              value={c.banco_codigo}
              onChange={(e) => setC((x) => ({ ...x, banco_codigo: e.target.value.replace(/\D/g, "").slice(0, 3) }))}
              error={codigoInvalido}
              helperText={codigoInvalido ? "3 dígitos" : "Ex.: 341"}
              fullWidth
              slotProps={{ htmlInput: { inputMode: "numeric" } }}
            />
          </Grid>
          <Grid size={{ xs: 4 }}>
            <TextField label="Agência" value={c.agencia} onChange={muda("agencia")} required fullWidth />
          </Grid>
          <Grid size={{ xs: 4 }}>
            <TextField label="Conta" value={c.conta} onChange={muda("conta")} required fullWidth />
          </Grid>
          <Grid size={{ xs: 4 }}>
            <TextField select label="Tipo" value={c.tipo} onChange={muda("tipo")} fullWidth>
              {TIPOS_CONTA.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={12}>
            <TextField label="PIX (opcional)" value={c.pix} onChange={muda("pix")} fullWidth />
          </Grid>
          <Grid size={12}>
            <TextField label="Observação (opcional)" value={c.observacao} onChange={muda("observacao")} fullWidth />
          </Grid>
          <Grid size={12}>
            <Stack direction="row" spacing={2}>
              <FormControlLabel
                control={<Switch checked={c.principal && c.ativo} disabled={!c.ativo} onChange={(e) => setC((x) => ({ ...x, principal: e.target.checked }))} />}
                label="Principal (sugerida na proposta)"
              />
              {conta && <FormControlLabel control={<Switch checked={c.ativo} onChange={(e) => setC((x) => ({ ...x, ativo: e.target.checked }))} />} label="Ativa" />}
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={aoFechar} disabled={gravando}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={() => void gravar()} disabled={gravando || !c.banco.trim() || !c.agencia.trim() || !c.conta.trim() || codigoInvalido}>
          {gravando ? "Gravando…" : "Gravar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function ContasBancarias() {
  const notify = useNotify();
  const { permissions } = usePermissions<Permissoes | null>();
  const admin = permissions?.papel === "admin";
  const { data: contas = [], refetch } = useGetList<ContaBancaria>("conta_bancaria", {
    pagination: { page: 1, perPage: 50 },
    sort: { field: "banco", order: "ASC" },
  });
  const [editando, setEditando] = useState<ContaBancaria | "nova" | null>(null);

  async function tornarPrincipal(c: ContaBancaria) {
    const { error } = await supabase.from("conta_bancaria").update({ principal: true }).eq("id", c.id);
    if (error) notify(`Não foi possível alterar: ${error.message}`, { type: "error" });
    else void refetch();
  }

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ fontSize: "1rem" }}>
            Contas bancárias
          </Typography>
          {admin && (
            <Button size="small" startIcon={<AddIcon />} onClick={() => setEditando("nova")}>
              Nova conta
            </Button>
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          A estrela marca a conta principal, sugerida na proposta. Ao gerar a proposta, dá para escolher outra.
        </Typography>
        {contas.length === 0 ? (
          <Alert severity="info">Nenhuma conta cadastrada.</Alert>
        ) : (
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Banco</TableCell>
                  <TableCell>Agência</TableCell>
                  <TableCell>Conta</TableCell>
                  <TableCell>PIX</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {contas.map((c) => (
                  <TableRow key={c.id} hover={admin} sx={{ opacity: c.ativo ? 1 : 0.5 }}>
                    <TableCell padding="checkbox">
                      <Tooltip title={c.principal ? "Conta principal" : admin && c.ativo ? "Tornar principal" : ""}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={!admin || c.principal || !c.ativo}
                            onClick={() => void tornarPrincipal(c)}
                            aria-label={c.principal ? "Conta principal" : "Tornar principal"}
                            sx={{ color: c.principal ? "warning.main" : "text.secondary" }}
                          >
                            {c.principal ? <EstrelaIcon fontSize="small" /> : <EstrelaVaziaIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {c.banco}
                      {c.banco_codigo ? ` (${c.banco_codigo})` : ""}
                    </TableCell>
                    <TableCell>{c.agencia}</TableCell>
                    <TableCell>
                      {TIPOS_CONTA.find((t) => t.id === c.tipo)?.name ?? c.tipo} {c.conta}
                    </TableCell>
                    <TableCell>{c.pix ?? ""}</TableCell>
                    <TableCell align="right">
                      {!c.ativo && <Chip size="small" variant="outlined" label="Inativa" sx={{ mr: 1 }} />}
                      {admin && (
                        <Button size="small" onClick={() => setEditando(c)}>
                          Editar
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
      {editando && (
        <DialogoConta
          conta={editando === "nova" ? null : editando}
          primeira={contas.filter((c) => c.ativo).length === 0}
          aoFechar={() => setEditando(null)}
          aoGravar={() => {
            setEditando(null);
            void refetch();
          }}
        />
      )}
    </Card>
  );
}
